import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import ViewportMgr from 'coreClasses/view/ViewportMgr';
import ComponentErrorBoundary from 'coreComponents/ComponentErrorBoundary';
import {
	useDebouncedValue,
	useDeferredRoute,
	useLockScroll,
	useScrollDirection,
	useScrollThreshold,
	useSetUI,
	useUI,
} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import FlyoutToggleButton from 'brandComponents/flyouts/FlyoutToggleButton';
import Icon from 'brandComponents/Icon';
import Link from 'brandComponents/Link';
import ShowOrHideOnBreakpoint from 'brandComponents/Responsive/ShowOrHideOnBreakpoint';
import RotationalBanner from 'brandComponents/RotationalBanner';
import searchInputMessages from 'brandComponents/SearchInput/messages';
import {useOpenedFlyouts} from 'brandHooks';

import {HeaderProvider} from './HeaderCtx';
import {checkIsSameLevel} from './helpers';
import {IHeaderProps} from './IHeader';
import {FlyoutsContainer, MainNavigation, PopupsContainer, ServiceMenu} from './Subcomponents';

// eslint-disable-next-line no-restricted-imports
import './Header.scss';

const SearchInputPlaceholder = () => {
	const intl = useIntl();

	return (
		<div className="header__quick-search-trigger__wrapper">
			<div className="header__quick-search-trigger__icon" >
				<Icon name="search-grey" width={20} height={20} />
			</div>
			<span>
				{intl.formatMessage(searchInputMessages.search)}
			</span>
		</div>
	);
};

const Header = ({headerData, rotationalBannerData, livePreviewEntryId}: IHeaderProps) => {
	const {managerRepository} = useGeneralProvider();
	const setUI = useSetUI();
	const scrollDirection = useDebouncedValue(useScrollDirection(40), 50);
	const {lockScroll} = useLockScroll({
		lockerClassName: 'main-navigation-opened',
		restoreScrollPosition: true,
		delay: 50
	});
	const [isLoading, setIsLoading] = useState(true); // TODO: Find a better way to handle hydration process
	const [isMegamenuHidden, setIsMegamenuHidden] = useState(false);

	const isNavigationOpenedRef = useRef(false);
	const navigationStackRef = useRef<Record<string, string>>({});

	const headerUI = useUI('header');
	const locked = headerUI.locked;
	const isNavigationOpened = headerUI.isNavigationOpened;

	const scrolled = useDebouncedValue(useScrollThreshold(10), 50);

	const [isTransparent, setIsTransparent] = useState(true);
	const [hidden, setHidden] = useState(false);
	const isAnyFlyoutOpened = useOpenedFlyouts().isAnyFlyoutOpened;

	const handleNavigationItemClick = useCallback((newId: string, parentId: string) => () => {
		const tempStack = Array.from(Object.entries(navigationStackRef.current));
		const isSameLevel = checkIsSameLevel(parentId, tempStack);

		if (isSameLevel) {
			tempStack.splice(-1);
		} else {
			// Push a new item into the stack
			tempStack.push([newId, parentId]);
		}

		navigationStackRef.current = Object.fromEntries(tempStack);

		setUI.setNavigationStack(navigationStackRef.current);
	}, []);

	const handleNavigationGoBack = useCallback(() => {
		const isDesktop = ViewportMgr.isGreaterThan('xx-large');

		if (isDesktop) {
			return;
		}

		// Remove the last item in the stack
		const tempStack = Array.from(Object.entries(navigationStackRef.current)).slice(0, -1);

		navigationStackRef.current = Object.fromEntries(tempStack);
		setUI.setNavigationStack(navigationStackRef.current);
	}, []);

	const handleNavigationVisibility = useCallback(() => {
		const flyoutsMgr = managerRepository.getFlyoutsMgr();
		const isDesktop = ViewportMgr.isGreaterThan('xx-large');

		if (isDesktop) {
			setIsMegamenuHidden(true);
			lockScroll(false);

			return;
		}

		// Reset the navigation stack when the navigation is closed
		if (Object.keys(navigationStackRef.current || {}).length) {
			setUI.setNavigationStack({});
			navigationStackRef.current = {};
		}

		const newStatus = !isNavigationOpenedRef.current;

		if (!newStatus) {
			flyoutsMgr.closeFlyout('quickSearch');
		}

		isNavigationOpenedRef.current = newStatus;
		setUI.setHeader({isNavigationOpened: newStatus});
		lockScroll(newStatus);
		flyoutsMgr.closeAllFlyouts();
	}, []);

	const handleLogoClick = useCallback(() => {
		const isDesktop = ViewportMgr.isGreaterThan('xx-large');

		if (isDesktop) {
			return;
		}

		if (isNavigationOpenedRef.current) {
			handleNavigationVisibility();
		}
	}, []);

	const providerValue = useMemo(() => ({
		isInHeaderContext: true,
		setIsMegamenuHidden,
		handleNavigationItemClick,
		handleNavigationVisibility,
		handleNavigationGoBack,
	}), []);

	const {isHomePage} = useDeferredRoute();
	const LogoComponent = isHomePage ? 'h1' : 'div';

	useEffect(() => {
		if (isNavigationOpened !== isNavigationOpenedRef.current) {
			handleNavigationVisibility();
		}
	}, [isNavigationOpened]);

	useEffect(() => {
		if (locked) {
			setUI.setHeader({locked: false});

			return () => null;
		}

		if (scrolled && scrollDirection === 'down') {
			setUI.addHtmlCssClasses(['header-scrolled']);
			setHidden(true);
		} else {
			setUI.removeHtmlCssClasses(['header-scrolled']);
			setHidden(false);
		}
	}, [scrolled, scrollDirection]);

	useEffect(() => {
		const setTransparence = () => setIsTransparent(window.scrollY === 0);

		setIsLoading(false);
		window.addEventListener('scroll', setTransparence);

		return () => {
			window.removeEventListener('scroll', setTransparence);
		};
	}, []);

	return (
		<HeaderProvider value={providerValue}>
			<ComponentErrorBoundary>
				<header
					className={cn('header', {
						'loading': isLoading,
						'navigation-opened': isNavigationOpened,
						'flyouts-opened': isAnyFlyoutOpened,
						'scrolled': hidden,
						'megamenu_hidden': isMegamenuHidden,
						'header--transparent': isTransparent,
						'header--white': headerUI.type === 'white',
					})}
					onMouseEnter={() => isMegamenuHidden && setIsMegamenuHidden(false)}
				>
					<RotationalBanner
						rotationalBannerData={rotationalBannerData}
						livePreviewEntryId={livePreviewEntryId}
					/>
					<div className="header__content">
						<div className="header__content-wrapper">
							<LogoComponent className="header__logo">
								<Link to="/" onClick={handleLogoClick}>
									<Icon
										name="casadei-logo"
										classNames="header__logo-img"
										width={40}
										height={40}
									/>
								</Link>
							</LogoComponent>
							<MainNavigation data={headerData} />
						</div>
						<ServiceMenu classNames="service-menu"/>
					</div>
					<ShowOrHideOnBreakpoint query="<xx-large" classNames="header__quick-search-trigger">
						<FlyoutToggleButton
							buttonContent={<SearchInputPlaceholder />}
							flyoutId="quickSearch"

						/>
					</ShowOrHideOnBreakpoint>
					<PopupsContainer />
					<FlyoutsContainer />
				</header>
			</ComponentErrorBoundary>
		</HeaderProvider>
	);
};

export default Header;
